import { arcade, bindPointer } from './arcade.js';
"use strict";
    /* =====================================================================
       PAPERBOY  —  authentic 1985-style rebuild
       Pure vanilla JS + Canvas 2D, fully self-contained.

       Visual approach: a 3/4 OBLIQUE view. The world is laid out in
       "world" coordinates (a tall vertical strip that scrolls toward the
       camera). When we draw, every world point is run through worldToScreen()
       which applies a horizontal SHEAR based on depth so the lawns / street /
       houses recede diagonally up-and-back like the arcade original.
       Houses are drawn in oblique projection (front face + side face + roof).
       ===================================================================== */

    // ----- Canvas / context -------------------------------------------------
    const canvas = document.getElementById('gameCanvas');
    const ctx = canvas.getContext('2d');
    const overlay = document.getElementById('overlay');
    const startButton = document.getElementById('startButton');

    const GAME_WIDTH = canvas.width;     // 1000
    const GAME_HEIGHT = canvas.height;   // 700

    // ----- Reduced motion (live-updating) -----------------------------------
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let prefersReducedMotion = reducedMotionQuery.matches;
    reducedMotionQuery.addEventListener('change', (e) => { prefersReducedMotion = e.matches; });

    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    // =====================================================================
    //  OBLIQUE PROJECTION
    //  World space: x in [0..ROAD_W_TOTAL] across, y = depth (0 = far, grows
    //  toward camera). The camera shows a window of depth of height
    //  GAME_HEIGHT. We shear x by an amount proportional to how far "back"
    //  (small screenY) a point is, producing the receding parallelogram.
    // =====================================================================
    const WORLD_W = 520;                 // logical width of the playfield strip
    const SHEAR = 0.42;                   // how strongly the world slants right as it recedes
    const WORLD_SCALE_X = GAME_WIDTH / WORLD_W; // base horizontal scale

    // Lane boundaries in WORLD x (left grass | sidewalk | street | sidewalk | right grass)
    const LEFT_LAWN_X = 0;
    const LEFT_WALK_X = 110;
    const STREET_L_X = 150;
    const STREET_R_X = 370;
    const RIGHT_WALK_X = 410;
    const RIGHT_LAWN_X = 520;

    // Player rides on the sidewalk band; clamp within these world-x bounds.
    const PLAYER_MIN_X = 120;
    const PLAYER_MAX_X = 400;

    // Convert a world point (wx across, screenY vertical) to screen coords.
    // The amount of horizontal slide depends on screenY: things near the top
    // (far away) slide right; the shear pivots around the bottom of screen.
    function shearAt(screenY) {
        // depth 0 at bottom (screenY=GAME_HEIGHT) -> 1 at top (screenY=0)
        const depth = (GAME_HEIGHT - screenY) / GAME_HEIGHT;
        return depth * SHEAR * GAME_WIDTH;
    }
    function worldToScreenX(wx, screenY) {
        return wx * WORLD_SCALE_X + shearAt(screenY);
    }

    // =====================================================================
    //  PIXEL-SPRITE ENGINE
    //  Each sprite = { w, h, palette:{char:color}, rows:[strings...] }
    //  '.' = transparent. drawSprite paints integer-aligned rects so it stays
    //  crisp and chunky.
    // =====================================================================
    function drawSprite(sprite, ox, oy, scale, flip) {
        const rows = sprite.rows, pal = sprite.palette;
        const s = scale || 1;
        for (let r = 0; r < rows.length; r++) {
            const row = rows[r];
            for (let c = 0; c < row.length; c++) {
                const ch = row[c];
                if (ch === '.' || ch === ' ') continue;
                const color = pal[ch];
                if (!color) continue;
                ctx.fillStyle = color;
                const cx = flip ? (sprite.w - 1 - c) : c;
                ctx.fillRect(Math.round(ox + cx * s), Math.round(oy + r * s), Math.ceil(s), Math.ceil(s));
            }
        }
    }

    // ---- Tiny pixel font (5x7) for in-canvas text, no web fonts ------------
    const FONT = {
        'A':["01110","10001","10001","11111","10001","10001","10001"],
        'B':["11110","10001","10001","11110","10001","10001","11110"],
        'C':["01111","10000","10000","10000","10000","10000","01111"],
        'D':["11110","10001","10001","10001","10001","10001","11110"],
        'E':["11111","10000","10000","11110","10000","10000","11111"],
        'F':["11111","10000","10000","11110","10000","10000","10000"],
        'G':["01111","10000","10000","10111","10001","10001","01111"],
        'H':["10001","10001","10001","11111","10001","10001","10001"],
        'I':["11111","00100","00100","00100","00100","00100","11111"],
        'J':["00111","00010","00010","00010","00010","10010","01100"],
        'K':["10001","10010","10100","11000","10100","10010","10001"],
        'L':["10000","10000","10000","10000","10000","10000","11111"],
        'M':["10001","11011","10101","10101","10001","10001","10001"],
        'N':["10001","11001","10101","10011","10001","10001","10001"],
        'O':["01110","10001","10001","10001","10001","10001","01110"],
        'P':["11110","10001","10001","11110","10000","10000","10000"],
        'Q':["01110","10001","10001","10001","10101","10010","01101"],
        'R':["11110","10001","10001","11110","10100","10010","10001"],
        'S':["01111","10000","10000","01110","00001","00001","11110"],
        'T':["11111","00100","00100","00100","00100","00100","00100"],
        'U':["10001","10001","10001","10001","10001","10001","01110"],
        'V':["10001","10001","10001","10001","10001","01010","00100"],
        'W':["10001","10001","10001","10101","10101","11011","10001"],
        'X':["10001","10001","01010","00100","01010","10001","10001"],
        'Y':["10001","10001","01010","00100","00100","00100","00100"],
        'Z':["11111","00010","00100","01000","10000","10000","11111"],
        '0':["01110","10011","10101","10101","11001","10001","01110"],
        '1':["00100","01100","00100","00100","00100","00100","01110"],
        '2':["01110","10001","00001","00110","01000","10000","11111"],
        '3':["11110","00001","00001","01110","00001","00001","11110"],
        '4':["00010","00110","01010","10010","11111","00010","00010"],
        '5':["11111","10000","11110","00001","00001","10001","01110"],
        '6':["01110","10000","10000","11110","10001","10001","01110"],
        '7':["11111","00001","00010","00100","01000","01000","01000"],
        '8':["01110","10001","10001","01110","10001","10001","01110"],
        '9':["01110","10001","10001","01111","00001","00001","01110"],
        ':':["00000","00100","00100","00000","00100","00100","00000"],
        '/':["00001","00010","00010","00100","01000","01000","10000"],
        '!':["00100","00100","00100","00100","00100","00000","00100"],
        '+':["00000","00100","00100","11111","00100","00100","00000"],
        '-':["00000","00000","00000","11111","00000","00000","00000"],
        '.':["00000","00000","00000","00000","00000","00000","00100"],
        ',':["00000","00000","00000","00000","00100","00100","01000"],
        '%':["11001","11010","00100","01000","01011","10011","00000"],
        '?':["01110","10001","00001","00110","00100","00000","00100"],
        '\'':["00100","00100","00100","00000","00000","00000","00000"],
        ' ':["00000","00000","00000","00000","00000","00000","00000"]
    };
    function pixText(str, x, y, size, color) {
        size = size || 2;
        color = color || '#fff';
        ctx.fillStyle = color;
        str = String(str).toUpperCase();
        let cx = x;
        for (let i = 0; i < str.length; i++) {
            const g = FONT[str[i]] || FONT['?'];
            for (let r = 0; r < 7; r++) {
                for (let c = 0; c < 5; c++) {
                    if (g[r][c] === '1') {
                        ctx.fillRect(cx + c * size, y + r * size, size, size);
                    }
                }
            }
            cx += 6 * size;
        }
        return cx; // returns end x
    }
    function pixTextWidth(str, size) { return String(str).length * 6 * (size || 2); }
    function pixTextCentered(str, cx, y, size, color) {
        pixText(str, Math.round(cx - pixTextWidth(str, size) / 2), y, size, color);
    }

    // =====================================================================
    //  SPRITE DATA
    // =====================================================================
    // Player on a BMX bike, oblique/side view, faces right. ~20x18 grid.
    // Two pedal frames.
    const SPR_PLAYER = [
      { w:20, h:20, palette:{
            R:'#d23b1f', r:'#8c2412', S:'#f4c89a', H:'#2a2a35', h:'#101018',
            B:'#3a6cd0', b:'#274a96', W:'#f2f2f2', T:'#1a1a1a', Y:'#ffd23f' },
        rows:[
          "..........HHHH......",
          ".........HHHHHH.....",
          ".........HHSSH......",
          "..........SSSS......",
          "....RR....RRR.......",
          "...RRRR..RRRRR......",
          "..RRRRRRRRRRRRR.....",
          "..RRWWRRRRRRRR......",
          "...RWWR..bBBb......",
          "....SS...bBBb.......",
          "....SS....BB........",
          "...TTT....BB........",
          "..TT......SS........",
          ".TT.......SS........",
          "TT........TS........",
          "...................",
          ".TTT.......TTT.....",
          "TTTTT.....TTTTT....",
          "TTbTT.....TTbTT....",
          ".TTT.......TTT.....",
        ]},
      { w:20, h:20, palette:{
            R:'#d23b1f', r:'#8c2412', S:'#f4c89a', H:'#2a2a35', h:'#101018',
            B:'#3a6cd0', b:'#274a96', W:'#f2f2f2', T:'#1a1a1a', Y:'#ffd23f' },
        rows:[
          "..........HHHH......",
          ".........HHHHHH.....",
          ".........HHSSH......",
          "..........SSSS......",
          "....RR....RRR.......",
          "...RRRR..RRRRR......",
          "..RRRRRRRRRRRRR.....",
          "..RRWWRRRRRRRR......",
          "...RWWR..bBBb......",
          ".........bBBb.......",
          "..........BB........",
          "..........BB........",
          ".........SSS........",
          "........SS.SS.......",
          ".......SS...S.......",
          "...................",
          ".TTT.......TTT.....",
          "TTTTT.....TTTTT....",
          "TTbTT.....TTbTT....",
          ".TTT.......TTT.....",
        ]}
    ];

    // Tumble / wipeout sprite (player crashing).
    const SPR_PLAYER_CRASH = { w:20, h:20, palette:{
            R:'#d23b1f', S:'#f4c89a', H:'#2a2a35', B:'#3a6cd0', T:'#1a1a1a', Y:'#ffd23f' },
        rows:[
          "...................",
          "....Y.....Y........",
          "...Y.HHHH..Y.......",
          "....HHSSHH.........",
          "..RRRRRRRRRR.......",
          ".RRRBBBBBBRRR......",
          "RR.BBBBBBBB.RR.....",
          "S...BBBBBB...S.....",
          "SS..........SS.....",
          ".T...TTTT...T......",
          "TTT.TTTTTT.TTT.....",
          ".T..TTTTTT..T......",
          "....TTTTTT.........",
          "...Y......Y........",
          "..Y........Y.......",
          "...................",
          "...................",
          "...................",
          "...................",
          "...................",
        ]};

    // Dog (chases). 14x10
    const SPR_DOG = { w:14, h:10, palette:{ B:'#8a5a2b', D:'#5d3a18', K:'#1a1a1a', W:'#fff' },
        rows:[
          "..........BBB.",
          ".........BBBBB",
          "..BBBBBBBBBKBB",
          ".BBBBBBBBBBBWB",
          "BBBBBBBBBBBBBB",
          "BBBBBBBBBBBBB.",
          "DD.DD..DD.DD..",
          "DD.DD..DD.DD..",
          "..............",
          "..............",
        ]};

    // Walking person. 10x16
    const SPR_PERSON = { w:10, h:16, palette:{ S:'#f4c89a', H:'#5a3210', G:'#2f9e3f', P:'#6a4a8a', K:'#1a1a1a' },
        rows:[
          "...HHHH...",
          "..HHHHHH..",
          "..HSSSSH..",
          "...SSSS...",
          "....SS....",
          "...GGGG...",
          "..GGGGGG..",
          "..GGGGGG..",
          "..GGGGGG..",
          "..GGGGGG..",
          "...PPPP...",
          "...P..P...",
          "...P..P...",
          "...P..P...",
          "..KK..KK..",
          ".KK....KK.",
        ]};

    // Breakdancer (spinning on the ground). 14x12
    const SPR_BREAKER = { w:14, h:12, palette:{ S:'#f4c89a', R:'#e23b5f', B:'#2244cc', K:'#1a1a1a', Y:'#ffd23f' },
        rows:[
          "......SS......",
          ".....SSSS.....",
          "..K..RRRR..K..",
          ".KK.RRRRRR.KK.",
          "KK.RRRRRRRR.KK",
          "..BBBBBBBBBB..",
          ".BB.BBBBBB.BB.",
          "SS...BBBB...SS",
          ".S...B..B...S.",
          ".....K..K.....",
          "....KK..KK....",
          "..............",
        ]};

    // Skateboarder. 14x14
    const SPR_SKATER = { w:14, h:14, palette:{ S:'#f4c89a', R:'#ff7b39', G:'#39c08a', K:'#1a1a1a', W:'#aaa' },
        rows:[
          ".....SSS......",
          "....SSSSS.....",
          "....SRRRS.....",
          ".....RRR......",
          "...RRRRRRR....",
          "..RRRRRRRRR...",
          "...RRRRRRR....",
          "....GG.GG.....",
          "....GG.GG.....",
          "...GG...GG....",
          "..KKK...KKK...",
          ".WWWWWWWWWWWW.",
          "..O........O..",
          "..............",
        ], };
    SPR_SKATER.palette.O = '#333';

    // Tricycle kid. 14x14
    const SPR_TRIKE = { w:14, h:14, palette:{ S:'#f4c89a', H:'#c23', Y:'#ffd23f', R:'#e23b1f', K:'#1a1a1a' },
        rows:[
          "....HHHH......",
          "...HHHHHH.....",
          "...HSSSSH.....",
          "....SSSS......",
          "...YYYYYY.....",
          "..YYYYYYYY....",
          "...YYYYYY.....",
          "....RR.RR.....",
          "...RR...RR....",
          "..KKK...KKK...",
          ".KK.K...K.KK..",
          ".K..K...K..K..",
          "..KK.....KK...",
          "..............",
        ]};

    // Rolling tire. 12x12
    const SPR_TIRE = { w:12, h:12, palette:{ K:'#1a1a1a', G:'#555', D:'#333' },
        rows:[
          "...KKKKKK...",
          ".KKKDDDDKK..",
          ".KDDGGGGDDK.",
          "KDGGKKKKGGDK",
          "KDGKGGGGKGDK",
          "KDGKGGGGKGDK",
          "KDGKGGGGKGDK",
          "KDGGKKKKGGDK",
          ".KDDGGGGDDK.",
          ".KKKDDDDKK..",
          "...KKKKKK...",
          "............",
        ]};

    // Lawnmower (pushed). 16x12
    const SPR_MOWER = { w:16, h:12, palette:{ R:'#d22', G:'#444', K:'#1a1a1a', S:'#f4c89a', B:'#852' },
        rows:[
          "..............BB",
          ".............BB.",
          "............BB..",
          "...........BB...",
          "..RRRRRRRR.B....",
          ".RRRRRRRRRR.....",
          ".RRRRRRRRRR.....",
          ".GGGGGGGGGG.....",
          ".KKK....KKK.....",
          "KKKKK..KKKKK....",
          ".KKK....KKK.....",
          "................",
        ]};

    // Fire hydrant. 8x14
    const SPR_HYDRANT = { w:8, h:14, palette:{ R:'#d22', r:'#911', S:'#888' },
        rows:[
          "..RRRR..",
          ".RRRRRR.",
          ".RrRRrR.",
          "RRRRRRRR",
          "RRRRRRRR",
          ".RRRRRR.",
          "RRRRRRRR",
          ".RRRRRR.",
          ".RRRRRR.",
          "..RRRR..",
          "..RRRR..",
          ".SSSSSS.",
          "SSSSSSSS",
          "SSSSSSSS",
        ]};

    // Trash can. 10x12
    const SPR_TRASH = { w:10, h:12, palette:{ G:'#777', D:'#555', K:'#333' },
        rows:[
          ".KKKKKKKK.",
          "KKKKKKKKKK",
          ".DGDGDGDG.",
          ".GDGDGDGD.",
          ".DGDGDGDG.",
          ".GDGDGDGD.",
          ".DGDGDGDG.",
          ".GDGDGDGD.",
          ".DGDGDGDG.",
          ".GDGDGDGD.",
          ".DDDDDDDD.",
          ".KKKKKKKK.",
        ]};

    // Paper bundle pickup. 12x10
    const SPR_BUNDLE = { w:12, h:10, palette:{ W:'#f2f2f2', G:'#bbb', K:'#222', R:'#d22' },
        rows:[
          "............",
          ".WWWWWWWWWW.",
          ".WGGGGGGGGW.",
          ".WWWWWWWWWW.",
          ".WRRRRRRRRW.",
          ".WWWWWWWWWW.",
          ".WGGGGGGGGW.",
          ".WWWWWWWWWW.",
          ".KKKKKKKKKK.",
          "............",
        ]};

    // =====================================================================
    //  AUDIO (Web Audio chiptune, guarded)
    // =====================================================================
    let audioCtx = null;
    function initAudio() {
        if (audioCtx) return;
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (AC) audioCtx = new AC();
        } catch (e) { audioCtx = null; }
    }
    function resumeAudio() {
        try { if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume(); } catch (e) {}
    }
    function beep(freq, dur, type, vol, slideTo) {
        if (!audioCtx || !arcade.soundEnabled) return;
        try {
            const t = audioCtx.currentTime;
            const osc = audioCtx.createOscillator();
            const gain = audioCtx.createGain();
            osc.type = type || 'square';
            osc.frequency.setValueAtTime(freq, t);
            if (slideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, slideTo), t + dur);
            gain.gain.setValueAtTime(0.0001, t);
            gain.gain.exponentialRampToValueAtTime((vol || 0.15), t + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
            osc.connect(gain); gain.connect(audioCtx.destination);
            osc.start(t); osc.stop(t + dur + 0.02);
        } catch (e) {}
    }
    function sfxThrow()   { beep(520, 0.12, 'triangle', 0.12, 240); }
    function sfxDeliver() { beep(660, 0.09, 'square', 0.16); setTimeout(()=>beep(990,0.12,'square',0.16),70); }
    function sfxPerfect() { beep(784,0.08,'square',0.16); setTimeout(()=>beep(988,0.08,'square',0.16),70); setTimeout(()=>beep(1318,0.16,'square',0.16),140); }
    function sfxSmash()   { beep(180, 0.18, 'sawtooth', 0.16, 60); beep(1200,0.05,'square',0.08); }
    function sfxCrash()   { beep(140, 0.4, 'sawtooth', 0.2, 40); }
    function sfxPickup()  { beep(880, 0.07, 'square', 0.14); setTimeout(()=>beep(1175,0.09,'square',0.14),60); }
    function sfxBadHit()  { beep(220, 0.22, 'square', 0.16, 110); }
    function sfxDayDone() {
        const notes = [523,659,784,1047];
        notes.forEach((n,i)=>setTimeout(()=>beep(n,0.16,'square',0.16),i*120));
    }

    // =====================================================================
    //  GAME STATE
    // =====================================================================
    let gameState = 'title'; // title | banner | playing | crashing | dayComplete | gameOver
    let currentDay = 0;
    let completedDays = 0;
    let deliveryStreak = 0;
    let bestStreak = 0;
    let score = 0;
    let lives = 3;
    let papers = 10;
    let subscribers = 5;          // current live subscriber count (persists across days)
    let highScore = 0;

    // per-day bookkeeping
    let daySubsTotal = 0;         // subscriber houses this day
    let dayDelivered = 0;         // successful subscriber deliveries this day
    let dayWindowsSmashed = 0;    // non-subscriber windows broken
    let dayPerfectThrows = 0;

    let houses = [];
    let entities = [];            // obstacles / hazards
    let bundles = [];             // paper bundle pickups
    let flyingPapers = [];
    let particles = [];

    let worldScroll = 0;          // how far the world has scrolled toward us
    let streetLength = 0;         // depth at which the houses end
    let bannerTimer = 0;
    let crashTimer = 0;
    let pedalFrame = 0;
    let pedalAcc = 0;
    let messageTimer = 0;
    let messageText = '';

    const SPEED_MIN = 1.4;
    const SPEED_MAX = 6.5;

    // Player. x is WORLD x; depthY is fixed near bottom of screen.
    const player = {
        x: 130,                   // world x (on left sidewalk by default)
        targetLean: 0,
        lean: 0,
        speed: 3,
        screenY: GAME_HEIGHT - 120, // where the rider is drawn vertically
        invincible: false,
        invTimer: 0,
    };

    const keys = {};

    // ---- High score persistence -------------------------------------------
    try {
        const hs = localStorage.getItem('paperboyHighScore');
        if (hs) highScore = parseInt(hs, 10) || 0;
    } catch (e) {}
    function saveHighScore() {
        if (score > highScore) {
            highScore = score;
            try { localStorage.setItem('paperboyHighScore', String(highScore)); } catch (e) {}
        }
    }

    // =====================================================================
    //  LEVEL GENERATION
    // =====================================================================
    const HOUSE_PALETTES = [
        { wall:'#e86a6a', wall2:'#c44', roof:'#7a3b2a', door:'#5a3210' },
        { wall:'#6aa0e8', wall2:'#4a7ec4', roof:'#33506a', door:'#5a3210' },
        { wall:'#e8c46a', wall2:'#c4a044', roof:'#8a6a2a', door:'#5a3210' },
        { wall:'#8ad06a', wall2:'#6aa84a', roof:'#3b6a2a', door:'#5a3210' },
        { wall:'#d08ad0', wall2:'#a86aa8', roof:'#6a3b6a', door:'#5a3210' },
        { wall:'#e8e0d0', wall2:'#c4b8a0', roof:'#888', door:'#5a3210' },
    ];

    function generateLevel() {
        houses = [];
        entities = [];
        bundles = [];
        flyingPapers = [];
        particles = [];
        worldScroll = 0;
        deliveryStreak = 0;

        const diff = currentDay;                       // 0..6
        const houseCount = 7 + Math.min(currentDay, 4); // 7..11 houses per side
        const spacing = 320;                           // world depth between houses
        const startDepth = 360;

        // Decide which houses are subscribers. We must guarantee at least
        // `subscribers` subscriber houses exist (so the player can keep them
        // all) and ideally a couple spare non-subscribers to smash.
        // Build a flat list of slots then mark subscribers.
        const slots = [];
        for (let i = 0; i < houseCount; i++) {
            slots.push({ side: 'left',  depth: startDepth + i * spacing });
            slots.push({ side: 'right', depth: startDepth + i * spacing + spacing * 0.5 });
        }

        // Mark `subscribers` of them as subscribers (spread out), rest are not.
        const subsNeeded = Math.min(subscribers, slots.length);
        // pick evenly spaced indices for subscribers so they're distributed
        const idxs = [];
        for (let i = 0; i < slots.length; i++) idxs.push(i);
        // shuffle deterministically-ish
        for (let i = idxs.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const t = idxs[i]; idxs[i] = idxs[j]; idxs[j] = t;
        }
        const subSet = new Set(idxs.slice(0, subsNeeded));

        slots.forEach((slot, i) => {
            const isSub = subSet.has(i);
            const pal = HOUSE_PALETTES[Math.floor(Math.random() * HOUSE_PALETTES.length)];
            // World-x of the house front: out on the lawns beyond the sidewalk.
            const wx = slot.side === 'left' ? 40 : 440;
            houses.push({
                side: slot.side,
                wx: wx,
                depth: slot.depth,
                width: 70,
                pal: pal,
                isSubscriber: isSub,
                delivered: false,        // subscriber got their paper
                missedHandled: false,
                // non-subscriber windows: 2 panes, can be smashed
                window1: false,          // smashed?
                window2: false,
                subWindowHit: false,     // hit a subscriber's window (bad)
                flash: 0,                // brief highlight after an event
            });
        });

        streetLength = startDepth + (houseCount) * spacing + 260;
        daySubsTotal = houses.filter(h => h.isSubscriber).length;
        dayDelivered = 0;
        dayWindowsSmashed = 0;
        dayPerfectThrows = 0;

        // -------- Obstacles / hazards --------
        // Variety grows with the day. Each entity has a world depth & x.
        const hazardTypes = ['hydrant','trash','tire','dog','person','skater','car','breaker','trike','mower'];
        const baseCount = 10 + diff * 4;
        for (let i = 0; i < baseCount; i++) {
            // spread along the street depth, after a small safe zone
            const depth = 260 + Math.random() * (streetLength - 360);
            // choose a type weighted by day (later days unlock more dynamic ones)
            let pool;
            if (diff <= 1) pool = ['hydrant','trash','tire','dog','person','car'];
            else if (diff <= 3) pool = ['hydrant','trash','tire','dog','person','car','skater','trike'];
            else pool = hazardTypes;
            const type = pool[Math.floor(Math.random() * pool.length)];
            entities.push(makeEntity(type, depth));
        }

        // -------- Paper bundles (refills) --------
        const bundleCount = Math.max(2, 5 - Math.floor(diff / 2)); // scarcer later
        for (let i = 0; i < bundleCount; i++) {
            const depth = 320 + Math.random() * (streetLength - 500);
            // place on the street so they're grab-able
            const wx = STREET_L_X + 30 + Math.random() * (STREET_R_X - STREET_L_X - 60);
            bundles.push({ wx: wx, depth: depth, collected: false });
        }
    }

    function makeEntity(type, depth) {
        // Default placement on the street; some live on the sidewalk.
        let wx = STREET_L_X + 20 + Math.random() * (STREET_R_X - STREET_L_X - 40);
        let vx = 0, vd = 0, moving = false, chase = false, sprite = null, w = 12, h = 12;
        switch (type) {
            case 'car':
                wx = STREET_L_X + 30 + Math.random() * (STREET_R_X - STREET_L_X - 60);
                vd = (Math.random() < 0.5 ? 1 : -1) * (0.6 + currentDay * 0.12); // moves along depth
                moving = true; w = 34; h = 26; break;
            case 'dog':
                wx = (Math.random() < 0.5 ? STREET_L_X + 20 : STREET_R_X - 20);
                moving = true; chase = true; w = 14; h = 10; sprite = SPR_DOG; break;
            case 'person':
                wx = STREET_L_X + 20 + Math.random() * (STREET_R_X - STREET_L_X - 40);
                vx = (Math.random() < 0.5 ? 1 : -1) * 0.4; moving = true; w = 10; h = 16; sprite = SPR_PERSON; break;
            case 'skater':
                wx = STREET_L_X + 20 + Math.random() * (STREET_R_X - STREET_L_X - 40);
                vx = (Math.random() < 0.5 ? 1 : -1) * 1.1; moving = true; w = 14; h = 14; sprite = SPR_SKATER; break;
            case 'breaker':
                wx = STREET_L_X + 30 + Math.random() * (STREET_R_X - STREET_L_X - 60);
                w = 14; h = 12; sprite = SPR_BREAKER; break;
            case 'trike':
                wx = STREET_L_X + 20 + Math.random() * (STREET_R_X - STREET_L_X - 40);
                vx = (Math.random() < 0.5 ? 1 : -1) * 0.7; moving = true; w = 14; h = 14; sprite = SPR_TRIKE; break;
            case 'tire':
                wx = STREET_L_X + 20 + Math.random() * (STREET_R_X - STREET_L_X - 40);
                vx = (Math.random() < 0.5 ? 1 : -1) * 0.9; moving = true; w = 12; h = 12; sprite = SPR_TIRE; break;
            case 'mower':
                wx = (Math.random() < 0.5 ? STREET_L_X + 10 : STREET_R_X - 26);
                vx = (Math.random() < 0.5 ? 1 : -1) * 0.5; moving = true; w = 16; h = 12; sprite = SPR_MOWER; break;
            case 'hydrant':
                wx = (Math.random() < 0.5 ? STREET_L_X - 6 : STREET_R_X + 4); w = 8; h = 14; sprite = SPR_HYDRANT; break;
            case 'trash':
                wx = STREET_L_X + 10 + Math.random() * (STREET_R_X - STREET_L_X - 20); w = 10; h = 12; sprite = SPR_TRASH; break;
            default:
                w = 10; h = 12; sprite = SPR_TRASH; break;
        }
        return {
            type, wx, depth, vx, vd, moving, chase, sprite, w, h,
            carColor: ['#d23','#27c','#fb3','#2c6','#a3d','#e84'][Math.floor(Math.random()*6)],
            phase: Math.random() * Math.PI * 2,
        };
    }

    // =====================================================================
    //  COORDINATE HELPERS
    //  depth -> screenY. Things at depth==worldScroll are at the bottom
    //  (near camera); larger depth = higher up the screen (farther).
    // =====================================================================
    const DEPTH_TO_PX = 1.0;   // 1 world-depth unit ~ 1px vertically (tuned)
    function depthToScreenY(depth) {
        // when depth == worldScroll  -> bottom (GAME_HEIGHT)
        // increasing depth moves up the screen
        return GAME_HEIGHT - (depth - worldScroll) * DEPTH_TO_PX;
    }

    // =====================================================================
    //  DRAWING — WORLD / GROUND BANDS (sheared parallelograms)
    // =====================================================================
    function drawGround() {
        // Sky / backdrop at the very top
        ctx.fillStyle = '#7ec0ee';
        ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

        // Build the receding ground as a stack of horizontal slices; for each
        // screen row we compute the sheared world-x boundaries and paint the
        // lawn / sidewalk / street segments. Doing it per-slice gives smooth
        // diagonal edges (the oblique look) without per-pixel cost.
        const SLICE = 4;
        for (let sy = 0; sy < GAME_HEIGHT; sy += SLICE) {
            const yMid = sy + SLICE / 2;
            const depthFactor = (GAME_HEIGHT - yMid) / GAME_HEIGHT; // 0 bottom .. 1 top

            // horizontal screen positions of each world boundary at this row
            const xLeftLawnL = worldToScreenX(LEFT_LAWN_X, yMid);
            const xWalkL     = worldToScreenX(LEFT_WALK_X, yMid);
            const xStreetL   = worldToScreenX(STREET_L_X, yMid);
            const xStreetR   = worldToScreenX(STREET_R_X, yMid);
            const xWalkR     = worldToScreenX(RIGHT_WALK_X, yMid);
            const xRightLawnR= worldToScreenX(RIGHT_LAWN_X, yMid);

            // Lawn (slightly darker as it recedes for depth)
            const lawnShade = 90 + Math.floor(60 * (1 - depthFactor));
            const lawn = 'rgb(' + Math.floor(lawnShade*0.45) + ',' + (lawnShade+40) + ',' + Math.floor(lawnShade*0.42) + ')';
            ctx.fillStyle = lawn;
            // full row as lawn first (covers far left/right beyond playfield)
            ctx.fillRect(0, sy, GAME_WIDTH, SLICE);

            // Left sidewalk
            ctx.fillStyle = '#c9c4b4';
            ctx.fillRect(xWalkL, sy, xStreetL - xWalkL, SLICE);
            // Right sidewalk
            ctx.fillRect(xWalkR, sy, xRightLawnR - xWalkR + 0, SLICE);
            // (right sidewalk drawn to RIGHT_LAWN boundary; lawn beyond repainted next row band)
            ctx.fillStyle = '#c9c4b4';
            ctx.fillRect(xWalkR, sy, xRightLawnR - xWalkR, SLICE);

            // Street
            const streetShade = 70 + Math.floor(30 * (1 - depthFactor));
            ctx.fillStyle = 'rgb(' + streetShade + ',' + streetShade + ',' + (streetShade+6) + ')';
            ctx.fillRect(xStreetL, sy, xStreetR - xStreetL, SLICE);

            // Re-cover far right lawn (street drawing may have over-extended none, safe)
        }

        // Sidewalk seams (kerbs) and center dashes drawn as sheared lines
        drawGroundLines();
    }

    function drawGroundLines() {
        // Kerb lines between street and sidewalks
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#9a9486';
        drawWorldVLine(STREET_L_X);
        drawWorldVLine(STREET_R_X);
        ctx.strokeStyle = '#b8b2a2';
        drawWorldVLine(LEFT_WALK_X);
        drawWorldVLine(RIGHT_WALK_X);

        // Dashed center line of the street, scrolling toward camera.
        const centerX = (STREET_L_X + STREET_R_X) / 2;
        ctx.fillStyle = '#e8d24a';
        const dashWorld = 60;
        const phase = worldScroll % dashWorld;
        for (let d = -phase; d < GAME_HEIGHT / DEPTH_TO_PX + dashWorld; d += dashWorld) {
            const depth = worldScroll + d;
            const yTop = depthToScreenY(depth + 26);
            const yBot = depthToScreenY(depth);
            if (yBot < -20 || yTop > GAME_HEIGHT + 20) continue;
            const xTop = worldToScreenX(centerX, yTop);
            const xBot = worldToScreenX(centerX, yBot);
            ctx.beginPath();
            ctx.moveTo(xTop - 3, yTop);
            ctx.lineTo(xTop + 3, yTop);
            ctx.lineTo(xBot + 4, yBot);
            ctx.lineTo(xBot - 4, yBot);
            ctx.closePath();
            ctx.fill();
        }
    }

    // Draw a vertical-in-world line across the whole screen (sheared).
    function drawWorldVLine(wx) {
        ctx.beginPath();
        let first = true;
        for (let sy = 0; sy <= GAME_HEIGHT; sy += 8) {
            const x = worldToScreenX(wx, sy);
            if (first) { ctx.moveTo(x, sy); first = false; }
            else ctx.lineTo(x, sy);
        }
        ctx.stroke();
    }

    // =====================================================================
    //  DRAWING — HOUSES (oblique projection)
    // =====================================================================
    function drawHouse(h) {
        const screenY = depthToScreenY(h.depth);
        if (screenY < -200 || screenY > GAME_HEIGHT + 200) return;

        // Anchor: front-bottom-left corner of the house in screen space.
        const baseX = worldToScreenX(h.wx, screenY);
        // Scale a touch by depth so distant houses are a little smaller.
        const depthF = Math.max(0.6, Math.min(1.15, (GAME_HEIGHT - screenY) / GAME_HEIGHT * 0.7 + 0.65));
        const W = h.width * depthF;          // front face width
        const H = 64 * depthF;               // wall height
        const D = 30 * depthF;               // oblique depth (side face)
        const dx = (h.side === 'left' ? -1 : 1); // which way the side face extends
        // For oblique we offset back-and-up. Side face goes "into" the screen:
        const ox = D * 0.9 * (h.side === 'left' ? 1 : -1);
        const oy = -D * 0.8;

        const x = baseX;
        const yBottom = screenY;
        const yTop = screenY - H;
        const pal = h.pal;

        // ---- side face (drawn first, behind front) ----
        ctx.fillStyle = shade(pal.wall2, h.flash > 0 ? 1.25 : 1);
        ctx.beginPath();
        if (h.side === 'left') {
            // side face on the right of the front face
            ctx.moveTo(x + W, yBottom);
            ctx.lineTo(x + W + ox, yBottom + oy);
            ctx.lineTo(x + W + ox, yTop + oy);
            ctx.lineTo(x + W, yTop);
        } else {
            ctx.moveTo(x, yBottom);
            ctx.lineTo(x + ox, yBottom + oy);
            ctx.lineTo(x + ox, yTop + oy);
            ctx.lineTo(x, yTop);
        }
        ctx.closePath();
        ctx.fill();

        // ---- front face ----
        ctx.fillStyle = shade(pal.wall, h.flash > 0 ? 1.2 : 1);
        ctx.fillRect(x, yTop, W, H);

        // ---- roof (pitched) ----
        // Front gable triangle + sloped top to the back.
        const ridge = 26 * depthF;
        ctx.fillStyle = pal.roof;
        // top slab over front+side
        ctx.beginPath();
        ctx.moveTo(x, yTop);
        ctx.lineTo(x + W, yTop);
        ctx.lineTo(x + W + ox, yTop + oy);
        ctx.lineTo(x + ox, yTop + oy);
        ctx.closePath();
        ctx.fill();
        // front gable
        ctx.fillStyle = shade(pal.roof, 1.15);
        ctx.beginPath();
        ctx.moveTo(x, yTop);
        ctx.lineTo(x + W, yTop);
        ctx.lineTo(x + W / 2, yTop - ridge);
        ctx.closePath();
        ctx.fill();

        // ---- door ----
        const doorW = W * 0.26, doorH = H * 0.5;
        const doorX = x + W * 0.37, doorY = yBottom - doorH;
        ctx.fillStyle = pal.door;
        ctx.fillRect(doorX, doorY, doorW, doorH);
        ctx.fillStyle = '#ffd23f';
        ctx.fillRect(doorX + doorW - 4 * depthF, doorY + doorH * 0.5, 2 * depthF, 2 * depthF);

        // ---- windows ----
        const winW = W * 0.2, winH = H * 0.24;
        const w1x = x + W * 0.1, w2x = x + W * 0.68, wy = yTop + H * 0.22;
        drawWindow(w1x, wy, winW, winH, h, 1);
        drawWindow(w2x, wy, winW, winH, h, 2);

        if (h.isSubscriber) {
            // ---- mailbox out front (target) ----
            const mbX = (h.side === 'left') ? (x + W + 10 * depthF) : (x - 16 * depthF);
            const mbY = yBottom - 22 * depthF;
            ctx.fillStyle = '#7a4a2a';
            ctx.fillRect(mbX + 4 * depthF, mbY + 10 * depthF, 3 * depthF, 14 * depthF); // post
            ctx.fillStyle = h.delivered ? '#36d66a' : '#d23b3b';
            ctx.fillRect(mbX, mbY, 14 * depthF, 10 * depthF);                            // box
            ctx.fillStyle = '#ffd23f';
            ctx.fillRect(mbX + 12 * depthF, mbY + 1 * depthF, 2 * depthF, 5 * depthF);   // flag

            // ---- doormat / porch target ----
            const matX = doorX - 2 * depthF, matY = yBottom - 3 * depthF;
            ctx.fillStyle = h.delivered ? '#36d66a' : '#b98a5a';
            ctx.fillRect(matX, matY, doorW + 6 * depthF, 4 * depthF);

            if (h.delivered) {
                pixTextCentered('OK', x + W / 2, yTop - ridge - 16, 2, '#36d66a');
            }
        } else {
            // Non-subscriber: subtle "no" marker so the player knows to smash.
            if (h.window1 && h.window2) {
                pixTextCentered('SMASH', x + W / 2, yTop - ridge - 16, 2, '#ff5b5b');
            }
        }
    }

    function drawWindow(wx, wy, ww, wh, h, idx) {
        const smashed = (idx === 1) ? h.window1 : h.window2;
        if (h.isSubscriber) {
            // subscriber windows are NOT targets; show hit (bad) as cracked red
            if (h.subWindowHit) {
                ctx.fillStyle = '#3a3a3a';
                ctx.fillRect(wx, wy, ww, wh);
                ctx.strokeStyle = '#ff5b5b'; ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(wx, wy); ctx.lineTo(wx + ww, wy + wh);
                ctx.moveTo(wx + ww, wy); ctx.lineTo(wx, wy + wh);
                ctx.stroke();
            } else {
                ctx.fillStyle = '#bfe6ff';
                ctx.fillRect(wx, wy, ww, wh);
                ctx.fillStyle = 'rgba(255,255,255,0.5)';
                ctx.fillRect(wx, wy, ww * 0.45, wh * 0.45);
            }
        } else {
            if (smashed) {
                // shattered: dark hole + jagged glass
                ctx.fillStyle = '#1c1c22';
                ctx.fillRect(wx, wy, ww, wh);
                ctx.strokeStyle = '#bfe6ff'; ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(wx, wy + wh*0.3); ctx.lineTo(wx + ww*0.4, wy + wh*0.6);
                ctx.lineTo(wx + ww*0.2, wy + wh); ctx.moveTo(wx + ww, wy);
                ctx.lineTo(wx + ww*0.6, wy + wh*0.5);
                ctx.stroke();
            } else {
                ctx.fillStyle = '#bfe6ff';
                ctx.fillRect(wx, wy, ww, wh);
                ctx.fillStyle = 'rgba(255,255,255,0.5)';
                ctx.fillRect(wx, wy, ww * 0.45, wh * 0.45);
            }
        }
        // frame
        ctx.strokeStyle = 'rgba(0,0,0,0.4)';
        ctx.lineWidth = 1;
        ctx.strokeRect(wx, wy, ww, wh);
    }

    // brighten/darken a hex color by factor
    function shade(hex, f) {
        if (f === 1) return hex;
        const n = parseInt(hex.slice(1), 16);
        let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
        r = Math.min(255, Math.floor(r * f));
        g = Math.min(255, Math.floor(g * f));
        b = Math.min(255, Math.floor(b * f));
        return 'rgb(' + r + ',' + g + ',' + b + ')';
    }

    // =====================================================================
    //  DRAWING — ENTITIES / BUNDLES / PAPERS / PLAYER
    // =====================================================================
    function drawEntity(e) {
        const screenY = depthToScreenY(e.depth);
        if (screenY < -60 || screenY > GAME_HEIGHT + 60) return;
        const sx = worldToScreenX(e.wx, screenY);
        const depthF = Math.max(0.7, Math.min(1.2, (GAME_HEIGHT - screenY) / GAME_HEIGHT * 0.6 + 0.7));
        const scale = depthF * 2;

        // shadow
        ctx.fillStyle = 'rgba(0,0,0,0.18)';
        ctx.beginPath();
        ctx.ellipse(sx, screenY + 2, e.w * scale * 0.45, 4 * depthF, 0, 0, Math.PI * 2);
        ctx.fill();

        if (e.type === 'car') {
            drawCar(e, sx, screenY, scale);
            return;
        }
        if (e.sprite) {
            const ox = sx - (e.sprite.w * scale) / 2;
            const oy = screenY - (e.sprite.h * scale);
            const flip = e.vx < 0;
            drawSprite(e.sprite, ox, oy, scale, flip);
        }
    }

    function drawCar(e, sx, sy, scale) {
        const w = 36 * scale * 0.5, h = 22 * scale * 0.5;
        const x = sx - w / 2, y = sy - h;
        // body
        ctx.fillStyle = e.carColor;
        ctx.fillRect(x, y, w, h);
        // roof/cabin
        ctx.fillStyle = shade(e.carColor, 0.8);
        ctx.fillRect(x + w * 0.18, y - h * 0.4, w * 0.64, h * 0.45);
        // windows
        ctx.fillStyle = '#bfe6ff';
        ctx.fillRect(x + w * 0.22, y - h * 0.32, w * 0.24, h * 0.32);
        ctx.fillStyle = '#bfe6ff';
        ctx.fillRect(x + w * 0.54, y - h * 0.32, w * 0.24, h * 0.32);
        // wheels
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(x + w * 0.1, y + h - 2, w * 0.22, 4 * scale * 0.5);
        ctx.fillRect(x + w * 0.68, y + h - 2, w * 0.22, 4 * scale * 0.5);
        // headlight
        ctx.fillStyle = '#fff3a0';
        ctx.fillRect(e.vd >= 0 ? x + w - 3 : x, y + h * 0.3, 3, 4);
    }

    function drawBundle(b) {
        if (b.collected) return;
        const screenY = depthToScreenY(b.depth);
        if (screenY < -40 || screenY > GAME_HEIGHT + 40) return;
        const sx = worldToScreenX(b.wx, screenY);
        const depthF = Math.max(0.7, Math.min(1.2, (GAME_HEIGHT - screenY) / GAME_HEIGHT * 0.6 + 0.7));
        const scale = depthF * 2;
        // bob
        const bob = prefersReducedMotion ? 0 : Math.sin(Date.now() / 200 + b.depth) * 2;
        ctx.fillStyle = 'rgba(0,0,0,0.18)';
        ctx.beginPath();
        ctx.ellipse(sx, screenY + 2, 10 * depthF, 3 * depthF, 0, 0, Math.PI * 2);
        ctx.fill();
        drawSprite(SPR_BUNDLE, sx - SPR_BUNDLE.w * scale / 2, screenY - SPR_BUNDLE.h * scale + bob, scale, false);
    }

    function drawFlyingPaper(p) {
        // paper has its own depth too (so it scrolls with the world)
        const screenY = depthToScreenY(p.depth) - p.lift;
        const sx = worldToScreenX(p.wx, depthToScreenY(p.depth)) + 0;
        ctx.save();
        ctx.translate(sx, screenY);
        ctx.rotate(p.rot);
        ctx.fillStyle = '#f4f4f4';
        ctx.fillRect(-6, -4, 12, 8);
        ctx.fillStyle = '#bbb';
        ctx.fillRect(-6, -4, 12, 2);
        ctx.strokeStyle = '#333'; ctx.lineWidth = 1;
        ctx.strokeRect(-6, -4, 12, 8);
        ctx.restore();
    }

    function drawParticles() {
        for (const pt of particles) {
            ctx.globalAlpha = Math.max(0, pt.life / pt.maxLife);
            ctx.fillStyle = pt.color;
            ctx.fillRect(pt.x, pt.y, pt.size, pt.size);
        }
        ctx.globalAlpha = 1;
    }

    function drawPlayer() {
        const screenY = player.screenY;
        const sx = worldToScreenX(player.x, screenY);

        if (gameState === 'crashing') {
            // tumble: spin the crash sprite
            ctx.save();
            ctx.translate(sx, screenY - 14);
            if (!prefersReducedMotion) ctx.rotate((1 - crashTimer / 60) * Math.PI * 3);
            drawSprite(SPR_PLAYER_CRASH, -SPR_PLAYER_CRASH.w, -SPR_PLAYER_CRASH.h, 2, false);
            ctx.restore();
            return;
        }

        // invincible flashing (calmed under reduced motion)
        let alpha = 1;
        if (player.invincible) {
            if (prefersReducedMotion) {
                alpha = 0.55;
            } else if (Math.floor(Date.now() / 100) % 2 === 0) {
                return;
            }
        }

        // shadow
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.beginPath();
        ctx.ellipse(sx, screenY + 2, 18, 5, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(sx, screenY);
        // lean when steering
        ctx.rotate(player.lean * 0.18);
        const spr = SPR_PLAYER[pedalFrame];
        drawSprite(spr, -spr.w, -spr.h * 2 + 4, 2, false);
        ctx.restore();
        ctx.globalAlpha = 1;
    }

    // =====================================================================
    //  PARTICLES
    // =====================================================================
    function spawnParticles(x, y, color, count, spread) {
        if (prefersReducedMotion) count = Math.min(count, 4);
        for (let i = 0; i < count; i++) {
            particles.push({
                x: x, y: y,
                vx: (Math.random() - 0.5) * (spread || 4),
                vy: (Math.random() - 0.5) * (spread || 4) - 1,
                size: 2 + Math.random() * 2,
                color: color,
                life: 30, maxLife: 30,
            });
        }
    }
    function updateParticles() {
        for (let i = particles.length - 1; i >= 0; i--) {
            const p = particles[i];
            p.x += p.vx; p.y += p.vy; p.vy += 0.2; p.life--;
            if (p.life <= 0) particles.splice(i, 1);
        }
    }

    // =====================================================================
    //  THROWING + DELIVERY
    //  A thrown paper gets a world depth (= where the rider is) plus a 'lift'
    //  arc. It travels sideways in world-x and slightly forward in depth so
    //  it reaches the houses. We test it against mailbox/porch/window hit
    //  rectangles each frame.
    // =====================================================================
    function throwPaper(dir) {
        if (papers <= 0) { sfxBadHit(); flashMessage('OUT OF PAPERS'); return; }
        papers--;
        updateHUD();
        sfxThrow();
        const playerDepth = worldScroll + (GAME_HEIGHT - player.screenY) / DEPTH_TO_PX;
        flyingPapers.push({
            wx: player.x,
            depth: playerDepth,
            dir: dir,                       // -1 left, +1 right
            vx: dir === 'left' ? -7.5 : 7.5,
            vd: 3.2,                        // forward (into screen) speed -> reaches houses
            lift: 0,
            vlift: 7.5,                     // initial upward arc
            rot: 0,
            rotSpeed: dir === 'left' ? -0.4 : 0.4,
            life: 90,
            hit: false,
        });
    }

    function updateFlyingPapers() {
        for (let i = flyingPapers.length - 1; i >= 0; i--) {
            const p = flyingPapers[i];
            p.wx += (p.dir === 'left' ? -1 : 1) * Math.abs(p.vx);
            p.depth += p.vd;
            p.lift += p.vlift;
            p.vlift -= 0.55;                // gravity on the arc
            p.rot += p.rotSpeed;
            p.life--;

            // Off-world horizontally or expired
            if (p.life <= 0 || p.wx < -40 || p.wx > WORLD_W + 40) {
                flyingPapers.splice(i, 1);
                continue;
            }

            // Test against houses on the matching side.
            let consumed = false;
            for (const h of houses) {
                if (h.side === 'left' && p.dir !== 'left') continue;
                if (h.side === 'right' && p.dir !== 'right') continue;
                // depth proximity: paper must be near the house depth band
                const dDepth = Math.abs(p.depth - h.depth);
                if (dDepth > 90) continue;

                if (h.isSubscriber && !h.delivered) {
                    // Target zone: a generous world-x window around the front
                    // of the house (mailbox + porch). Tuned to feel fair.
                    const targetMin = h.wx - 20;
                    const targetMax = h.wx + h.width + 30;
                    // Only a low throw lands on the porch/mailbox; a high-arcing
                    // paper (lift > 30) falls through to the window-hit band
                    // below, so smashing a subscriber's own window is reachable.
                    if (p.wx >= targetMin && p.wx <= targetMax && p.lift <= 30 && dDepth < 70) {
                        // perfect if close to the doormat depth & center
                        const perfect = dDepth < 34;
                        h.delivered = true;
                        h.flash = 14;
                        dayDelivered++;
                        deliveryStreak++;
                        bestStreak = Math.max(bestStreak, deliveryStreak);
                        const streakBonus = Math.min(5, deliveryStreak - 1) * 25;
                        score += streakBonus;
                        if (perfect) { dayPerfectThrows++; score += 250; sfxPerfect(); flashMessage('PERFECT!'); }
                        else { score += 100; sfxDeliver(); flashMessage('DELIVERED!'); }
                        if (streakBonus) flashMessage(`${deliveryStreak} IN A ROW! +${streakBonus}`);
                        spawnParticles(worldToScreenX(h.wx + h.width/2, depthToScreenY(h.depth)),
                                       depthToScreenY(h.depth) - 20, '#ffd23f', 10, 5);
                        updateHUD();
                        consumed = true;
                        break;
                    }
                    // Hitting the subscriber's WINDOW (bad) — narrower upper band
                    const winMin = h.wx + h.width * 0.05;
                    const winMax = h.wx + h.width * 0.9;
                    if (!h.delivered && p.wx >= winMin && p.wx <= winMax && p.lift > 30 && dDepth < 40) {
                        h.subWindowHit = true;
                        deliveryStreak = 0;
                        h.flash = 14;
                        score = Math.max(0, score - 50);
                        sfxBadHit();
                        flashMessage('OOPS! WRONG WINDOW');
                        spawnParticles(worldToScreenX(h.wx + h.width/2, depthToScreenY(h.depth)),
                                       depthToScreenY(h.depth) - 30, '#bfe6ff', 8, 4);
                        updateHUD();
                        consumed = true;
                        break;
                    }
                } else if (!h.isSubscriber) {
                    // Smash a window: aim high (lift) at the wall band.
                    const winMin = h.wx + h.width * 0.0;
                    const winMax = h.wx + h.width * 1.0;
                    if (p.wx >= winMin && p.wx <= winMax && dDepth < 60) {
                        if (!h.window1) {
                            h.window1 = true;
                        } else if (!h.window2) {
                            h.window2 = true;
                        } else {
                            continue; // already fully smashed; let paper pass
                        }
                        h.flash = 14;
                        dayWindowsSmashed++;
                        score += 75;
                        sfxSmash();
                        spawnParticles(worldToScreenX(h.wx + h.width/2, depthToScreenY(h.depth)),
                                       depthToScreenY(h.depth) - 28, '#bfe6ff', 12, 6);
                        updateHUD();
                        consumed = true;
                        break;
                    }
                }
            }
            if (consumed) flyingPapers.splice(i, 1);
        }
    }

    // =====================================================================
    //  UPDATE LOOP PIECES
    // =====================================================================
    function updatePlayer(dt) {
        // Speed control (up/down)
        if (keys['ArrowUp'] || keys['w'] || keys['W']) {
            player.speed = Math.min(player.speed + 0.08, SPEED_MAX);
        } else if (keys['ArrowDown'] || keys['s'] || keys['S']) {
            player.speed = Math.max(player.speed - 0.1, SPEED_MIN);
        } else {
            // drift toward a comfortable cruise
            if (player.speed > 3.2) player.speed -= 0.02;
            else if (player.speed < 3.0) player.speed += 0.02;
        }

        // Steering (left/right) in world-x
        let steer = 0;
        if (keys['ArrowLeft'] || keys['a'] || keys['A']) steer -= 1;
        if (keys['ArrowRight'] || keys['d'] || keys['D']) steer += 1;
        player.x += steer * 3.0;
        player.x = Math.max(PLAYER_MIN_X, Math.min(PLAYER_MAX_X, player.x));

        // Lean toward steer direction (smoothed)
        player.targetLean = steer;
        player.lean += (player.targetLean - player.lean) * 0.25;

        // Scroll world toward camera based on speed
        worldScroll += player.speed;

        // Pedaling animation tied to speed
        pedalAcc += player.speed;
        if (pedalAcc > 14) { pedalFrame = 1 - pedalFrame; pedalAcc = 0; }

        // Invincibility timer
        if (player.invincible) {
            player.invTimer--;
            if (player.invTimer <= 0) player.invincible = false;
        }

        // throw inputs (single-press handled via key reset)
        if (keys[' '] === true) { throwPaper('left'); keys[' '] = false; }
        if (keys['Enter'] === true) { throwPaper('right'); keys['Enter'] = false; }
        if (keys['Shift'] === true) { throwPaper('right'); keys['Shift'] = false; }
    }

    function updateEntities() {
        const playerDepth = worldScroll + (GAME_HEIGHT - player.screenY) / DEPTH_TO_PX;
        for (const e of entities) {
            // movement
            if (e.moving) {
                if (e.type === 'car') {
                    e.depth += e.vd * 3.0;
                    // keep cars roughly on street; wrap their depth within a window
                } else if (e.chase) {
                    // dog chases the player's world-x when nearby in depth
                    const near = Math.abs(e.depth - playerDepth) < 200;
                    if (near) {
                        const dir = Math.sign(player.x - e.wx) || 0;
                        e.wx += dir * 0.9;
                        // also creep toward camera a bit
                        e.depth -= 0.4;
                    }
                    e.wx = Math.max(STREET_L_X - 30, Math.min(STREET_R_X + 30, e.wx));
                } else {
                    e.wx += e.vx;
                    if (e.wx < STREET_L_X - 10 || e.wx > STREET_R_X + 10) e.vx *= -1;
                }
            }

            // collision with player (only when not invincible / not crashing)
            if (!player.invincible && gameState === 'playing') {
                const dDepth = Math.abs(e.depth - playerDepth);
                const dX = Math.abs(e.wx - player.x);
                if (dDepth < 22 && dX < 22) {
                    crash();
                }
            }
        }
    }

    function updateBundles() {
        const playerDepth = worldScroll + (GAME_HEIGHT - player.screenY) / DEPTH_TO_PX;
        for (const b of bundles) {
            if (b.collected) continue;
            const dDepth = Math.abs(b.depth - playerDepth);
            const dX = Math.abs(b.wx - player.x);
            if (dDepth < 26 && dX < 26) {
                b.collected = true;
                papers += 5;
                score += 25;
                sfxPickup();
                spawnParticles(worldToScreenX(b.wx, depthToScreenY(b.depth)),
                               depthToScreenY(b.depth) - 10, '#f4f4f4', 8, 4);
                updateHUD();
            }
        }
    }

    function updateHouseFlashes() {
        for (const h of houses) if (h.flash > 0) h.flash--;
    }

    function crash() {
        deliveryStreak = 0;
        gameState = 'crashing';
        crashTimer = 60;
        sfxCrash();
        const sx = worldToScreenX(player.x, player.screenY);
        spawnParticles(sx, player.screenY - 14, '#ffd23f', 14, 6);
        spawnParticles(sx, player.screenY - 14, '#fff', 10, 6);
    }

    function recoverFromCrash() {
        lives--;
        updateHUD();
        if (lives <= 0) {
            endGame();
            return;
        }
        player.invincible = true;
        player.invTimer = 120;
        player.speed = SPEED_MIN;
        gameState = 'playing';
    }

    // =====================================================================
    //  DAY FLOW
    // =====================================================================
    function checkDayComplete() {
        const playerDepth = worldScroll + (GAME_HEIGHT - player.screenY) / DEPTH_TO_PX;
        if (playerDepth > streetLength) {
            completeDay();
        }
    }

    function completeDay() {
        gameState = 'dayComplete';
        completedDays = currentDay + 1;
        sfxDayDone();
        saveHighScore();

        // Subscriber accounting:
        // - subscribers whose paper we missed may cancel
        // - subscribers whose own window we smashed cancel
        let lost = 0;
        for (const h of houses) {
            if (h.isSubscriber && (!h.delivered || h.subWindowHit)) {
                lost++;
            }
        }
        const perfectDay = (daySubsTotal > 0 && dayDelivered === daySubsTotal && lost === 0);

        let gained = 0;
        if (perfectDay && subscribers > 0) {
            gained = 1; // perfect day -> new subscriber moves in
        }

        subscribers = Math.max(0, subscribers - lost + gained);

        // bonus scoring
        let bonus = dayDelivered * 25 + dayWindowsSmashed * 10;
        if (perfectDay) bonus += 500;
        score += bonus;
        updateHUD();
        saveHighScore();

        showDayCompleteScreen({ lost, gained, perfectDay, bonus });
    }

    function showDayCompleteScreen(info) {
        const isLastDay = currentDay >= 6;
        const allLost = subscribers <= 0;
        let subLine;
        if (allLost) subLine = 'You lost all your subscribers!';
        else if (info.gained > 0) subLine = 'PERFECT DAY! A new subscriber moved in! (+1)';
        else if (info.lost > 0) subLine = info.lost + ' subscriber' + (info.lost > 1 ? 's' : '') + ' cancelled.';
        else subLine = 'No subscribers lost. Keep it up!';

        overlay.innerHTML =
            '<div class="overlay-title">' + DAYS[currentDay] + ' Done!</div>' +
            '<div class="stats-grid">' +
                '<div class="stat-item"><span class="stat-label">Deliveries:</span>' + info_dd() + '</div>' +
                '<div class="stat-item"><span class="stat-label">Windows Smashed:</span>' + dayWindowsSmashed + '</div>' +
                '<div class="stat-item"><span class="stat-label">Perfect Throws:</span>' + dayPerfectThrows + '</div>' +
                '<div class="stat-item"><span class="stat-label">Day Bonus:</span>+' + info.bonus + '</div>' +
                '<div class="stat-item"><span class="stat-label">Subscribers:</span>' + subscribers + '</div>' +
                '<div class="stat-item"><span class="stat-label">Total Score:</span>' + score + '</div>' +
            '</div>' +
            '<div class="overlay-text" style="color:#FFD700;">' + subLine + '</div>' +
            '<div class="overlay-text">' +
                (allLost ? 'The route is finished.' :
                 (isLastDay ? 'You survived the whole week!' : 'Get ready for ' + DAYS[currentDay + 1] + '!')) +
            '</div>' +
            '<button class="button" id="continueButton">' +
                ((allLost || isLastDay) ? 'See Results' : 'Next Day') + '</button>';
        overlay.classList.remove('hidden');
        updateMobileControlsVisibility();

        document.getElementById('continueButton').onclick = () => {
            if (allLost || isLastDay) endGame();
            else nextDay();
        };
    }
    function info_dd() { return dayDelivered + '/' + daySubsTotal; }

    function nextDay() {
        currentDay++;
        papers = Math.max(8, 12 - currentDay); // a bit scarcer each day
        player.x = 130;
        player.speed = 3;
        player.invincible = false;
        player.invTimer = 0;
        generateLevel();
        updateHUD();
        startDayBanner();
    }

    function startDayBanner() {
        overlay.classList.add('hidden');
        updateMobileControlsVisibility();
        gameState = 'banner';
        bannerTimer = prefersReducedMotion ? 60 : 110;
    }

    function endGame() {
        gameState = 'gameOver';
        saveHighScore();
        const wonWeek = completedDays === 7 && subscribers > 0 && lives > 0;
        let titleTxt, msg;
        if (wonWeek) { titleTxt = 'You Won The Week!'; msg = 'You kept your route alive all 7 days. Legendary paperboy!'; }
        else if (subscribers <= 0) { titleTxt = 'Route Lost'; msg = 'Every last subscriber cancelled. The paper let you go.'; }
        else { titleTxt = 'Game Over'; msg = 'You ran out of lives on ' + DAYS[currentDay] + '.'; }

        const newHigh = score >= highScore && score > 0;

        overlay.innerHTML =
            '<div class="overlay-title">' + titleTxt + '</div>' +
            '<div class="overlay-text">' + msg + '</div>' +
            '<div class="stats-grid">' +
                '<div class="stat-item"><span class="stat-label">Final Score:</span>' + score + '</div>' +
                '<div class="stat-item"><span class="stat-label">High Score:</span>' + highScore + '</div>' +
                '<div class="stat-item"><span class="stat-label">Days Done:</span>' + completedDays + '/7</div>' +
                '<div class="stat-item"><span class="stat-label">Best streak:</span>' + bestStreak + '</div>' +
            '</div>' +
            (newHigh ? '<div class="overlay-text" style="color:#FFD700;">NEW HIGH SCORE!</div>' : '') +
            '<button class="button" id="restartButton">Play Again</button>' +
            '<a href="../index.html" class="button red-btn">Back to Site</a>';
        overlay.classList.remove('hidden');
        updateMobileControlsVisibility();

        document.getElementById('restartButton').onclick = () => { startNewGame(); };
    }

    // =====================================================================
    //  HUD + MESSAGES
    // =====================================================================
    function updateHUD() {
        saveHighScore();
        document.getElementById('score').textContent = score;
        document.getElementById('papers').textContent = papers;
        document.getElementById('lives').textContent = lives;
        document.getElementById('day').textContent = DAYS[currentDay];
        document.getElementById('subscribers').textContent = subscribers;
    }

    function flashMessage(txt) {
        messageText = txt;
        messageTimer = 70;
    }

    function drawMessageBanner() {
        if (messageTimer <= 0) return;
        messageTimer--;
        const alpha = Math.min(1, messageTimer / 20);
        ctx.globalAlpha = alpha;
        const w = pixTextWidth(messageText, 3) + 24;
        const x = (GAME_WIDTH - w) / 2;
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(x, 70, w, 36);
        ctx.strokeStyle = '#ffd23f'; ctx.lineWidth = 2;
        ctx.strokeRect(x, 70, w, 36);
        pixTextCentered(messageText, GAME_WIDTH / 2, 80, 3, '#ffd23f');
        ctx.globalAlpha = 1;
    }

    // In-canvas mini HUD strip (so it reads even on mobile where the DOM HUD is tiny)
    function drawCanvasHUD() {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, GAME_WIDTH, 30);
        let x = 12;
        x = pixText('SCORE ' + score, x, 8, 2, '#ffd23f') + 18;
        x = pixText('PAPERS ' + papers, x, 8, 2, '#fff') + 18;
        x = pixText('SUBS ' + subscribers, x, 8, 2, '#7fffa0') + 18;
        x = pixText('LIVES ' + lives, x, 8, 2, '#ff7b7b') + 18;
        // day on the right
        pixText(DAYS[currentDay], GAME_WIDTH - pixTextWidth(DAYS[currentDay], 2) - 12, 8, 2, '#bfe6ff');
    }

    function drawDayBanner() {
        // big day name slides/fades in center
        const t = bannerTimer;
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, GAME_HEIGHT/2 - 60, GAME_WIDTH, 120);
        pixTextCentered(DAYS[currentDay], GAME_WIDTH/2, GAME_HEIGHT/2 - 40, 6, '#ffd23f');
        const sub = daySubsTotal + ' SUBSCRIBERS ON THE ROUTE';
        pixTextCentered(sub, GAME_WIDTH/2, GAME_HEIGHT/2 + 14, 2, '#fff');
        if (!prefersReducedMotion) {
            // little arrow blink
            if (Math.floor(t / 15) % 2 === 0)
                pixTextCentered('GET READY', GAME_WIDTH/2, GAME_HEIGHT/2 + 38, 2, '#7fffa0');
        } else {
            pixTextCentered('GET READY', GAME_WIDTH/2, GAME_HEIGHT/2 + 38, 2, '#7fffa0');
        }
    }

    // Optional subtle scanline/CRT overlay (respects reduced motion: static, faint)
    function drawScanlines() {
        if (prefersReducedMotion) return;
        ctx.globalAlpha = 0.06;
        ctx.fillStyle = '#000';
        for (let y = 0; y < GAME_HEIGHT; y += 3) {
            ctx.fillRect(0, y, GAME_WIDTH, 1);
        }
        ctx.globalAlpha = 1;
    }

    // =====================================================================
    //  RENDER: draw world back-to-front so nearer things overlap farther.
    // =====================================================================
    function render() {
        drawGround();

        // Sort drawables by depth (far first => larger depth drawn first).
        // Houses, entities, bundles all carry a depth; flying papers too.
        const drawList = [];
        for (const h of houses) drawList.push({ depth: h.depth, kind: 'house', ref: h });
        for (const e of entities) drawList.push({ depth: e.depth, kind: 'entity', ref: e });
        for (const b of bundles) if (!b.collected) drawList.push({ depth: b.depth, kind: 'bundle', ref: b });

        drawList_sortAndDraw(drawList);

        // Flying papers & particles on top
        for (const p of flyingPapers) drawFlyingPaper(p);
        drawParticles();

        // Player (always near camera)
        drawPlayer();

        // HUD overlays
        drawCanvasHUD();
        drawMessageBanner();

        if (gameState === 'banner') drawDayBanner();

        drawScanlines();
    }
    function drawList_sortAndDraw(list) {
        list.sort((a, b) => b.depth - a.depth); // far (big depth) first
        for (const item of list) {
            if (item.kind === 'house') drawHouse(item.ref);
            else if (item.kind === 'entity') drawEntity(item.ref);
            else if (item.kind === 'bundle') drawBundle(item.ref);
        }
    }

    // =====================================================================
    //  MAIN LOOP
    // =====================================================================
    // Fixed-timestep simulation. The game logic uses fixed per-frame deltas
    // tuned for 60Hz, so we advance it in discrete 1/60s steps independent of
    // the display refresh rate. Without this the whole game runs at 2x on a
    // 120Hz ProMotion screen (the mobile target) and 3x at 144Hz. Rendering
    // still happens once per animation frame.
    const SIM_STEP_MS = 1000 / 60;
    const MAX_STEPS_PER_FRAME = 5; // shed backlog after a long stall
    let lastTime = 0;
    let simAccumulator = 0;

    function stepSimulation() {
        if (gameState === 'playing') {
            updatePlayer();
            updateEntities();
            updateBundles();
            updateFlyingPapers();
            updateHouseFlashes();
            updateParticles();
            updateRoute();
            checkDayComplete();
        } else if (gameState === 'crashing') {
            crashTimer--;
            updateParticles();
            // keep papers/entities gently moving for life
            updateFlyingPapers();
            if (crashTimer <= 0) recoverFromCrash();
        } else if (gameState === 'banner') {
            bannerTimer--;
            updateParticles();
            if (bannerTimer <= 0) gameState = 'playing';
        } else {
            updateParticles();
        }
    }

    function gameLoop(timestamp) {
        if (!lastTime) lastTime = timestamp;
        let frameMs = timestamp - lastTime;
        lastTime = timestamp;
        if (frameMs > 100) frameMs = 100; // clamp big gaps (tab switch)

        if (arcade.paused) { simAccumulator = 0; requestAnimationFrame(gameLoop); return; }
        simAccumulator += frameMs;
        let steps = 0;
        while (simAccumulator >= SIM_STEP_MS && steps < MAX_STEPS_PER_FRAME) {
            stepSimulation();
            simAccumulator -= SIM_STEP_MS;
            steps++;
        }
        if (steps >= MAX_STEPS_PER_FRAME) simAccumulator = 0;

        if (gameState !== 'title') updateRoute();
        ctx.clearRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
        if (gameState === 'title') {
            // simple idle backdrop behind the overlay
            drawGround();
            drawCanvasHUD();
            drawScanlines();
        } else {
            render();
        }

        requestAnimationFrame(gameLoop);
    }

    // =====================================================================
    //  GAME SETUP / RESET
    // =====================================================================
    function updateRoute() {
        const depth = worldScroll + (GAME_HEIGHT - player.screenY) / DEPTH_TO_PX;
        for (const house of houses) {
            if (house.isSubscriber && !house.delivered && !house.missedHandled && house.depth < depth - 100) {
                house.missedHandled = true;
                deliveryStreak = 0;
                flashMessage('MISSED A SUBSCRIBER');
            }
        }
        const next = houses.filter(h => h.isSubscriber && !h.delivered && !h.missedHandled).sort((a,b) => a.depth - b.depth)[0];
        const target = next ? `Next delivery: ${next.side}` : 'Deliveries done. Finish the route.';
        arcade.status(`${dayDelivered}/${daySubsTotal} delivered · ${target} · Streak ${deliveryStreak} · Best ${highScore.toLocaleString()}`, depth / streetLength * 100);
    }

    function startNewGame() {
        initAudio(); resumeAudio();
        score = 0;
        lives = 3;
        currentDay = 0;
        completedDays = bestStreak = deliveryStreak = 0;
        subscribers = 5;
        papers = 12;
        player.x = 130;
        player.speed = 3;
        player.lean = 0;
        player.invincible = false;
        player.invTimer = 0;
        generateLevel();
        updateHUD();
        startDayBanner();
    }

    // =====================================================================
    //  INPUT
    // =====================================================================
    const GAME_KEYS = new Set([
        'ArrowUp','ArrowDown','ArrowLeft','ArrowRight',' ','Enter','Shift',
        'w','W','a','A','s','S','d','D'
    ]);

    document.addEventListener('keydown', (e) => {
        // Let keyboard users activate a focused overlay button/link (Start,
        // Continue, Restart, Back to Site) — don't swallow Enter/Space there.
        if (e.target && e.target.closest && e.target.closest('button, a')) return;
        // first interaction primes audio
        initAudio(); resumeAudio();
        if (GAME_KEYS.has(e.key)) {
            if (!e.repeat) keys[e.key] = true;
            // Space/Enter/Shift are single-press: consumed in updatePlayer.
            e.preventDefault();
        }
    });
    document.addEventListener('keyup', (e) => {
        if (e.target && e.target.closest && e.target.closest('button, a')) return;
        if (GAME_KEYS.has(e.key)) {
            // Don't un-set the throw keys here; they're cleared on consume.
            if (e.key !== ' ' && e.key !== 'Enter' && e.key !== 'Shift') {
                keys[e.key] = false;
            }
            e.preventDefault();
        }
    });

    startButton.onclick = () => {
        initAudio(); resumeAudio();
        startNewGame();
    };

    // =====================================================================
    //  MOBILE CONTROLS
    // =====================================================================
    const btnUp = document.getElementById('btnUp');
    const btnDown = document.getElementById('btnDown');
    const btnLeft = document.getElementById('btnLeft');
    const btnRight = document.getElementById('btnRight');
    const btnThrowLeft = document.getElementById('btnThrowLeft');
    const btnThrowRight = document.getElementById('btnThrowRight');

    // Continuous-press D-pad button
    function setupDpadButton(button, keyName) {
        const activate = (e) => {
            e.preventDefault();
            initAudio(); resumeAudio();
            keys[keyName] = true;
            button.classList.add('active');
        };
        const deactivate = (e) => {
            if (e) e.preventDefault();
            keys[keyName] = false;
            button.classList.remove('active');
        };
        bindPointer(button, () => activate({ preventDefault() {} }), () => deactivate({ preventDefault() {} }));
    }

    // Single-press action button (throw)
    function setupActionButton(button, keyName) {
        const activate = (e) => {
            e.preventDefault();
            initAudio(); resumeAudio();
            keys[keyName] = true;       // consumed next frame
            button.classList.add('active');
        };
        const deactivate = (e) => {
            if (e) e.preventDefault();
            button.classList.remove('active');
        };
        bindPointer(button, () => activate({ preventDefault() {} }), () => deactivate({ preventDefault() {} }));
    }

    setupDpadButton(btnUp, 'ArrowUp');
    setupDpadButton(btnDown, 'ArrowDown');
    setupDpadButton(btnLeft, 'ArrowLeft');
    setupDpadButton(btnRight, 'ArrowRight');
    setupActionButton(btnThrowLeft, ' ');
    setupActionButton(btnThrowRight, 'Enter');

    function updateMobileControlsVisibility() {
        const mobileControls = document.getElementById('mobileControls');
        const dialogVisible = !overlay.classList.contains('hidden');
        if (dialogVisible) mobileControls.classList.add('hidden');
        else mobileControls.classList.remove('hidden');
    }

    // Prevent scroll/zoom on the play area while playing
    document.getElementById('gameContainer').addEventListener('touchmove', (e) => {
        if (gameState === 'playing' || gameState === 'banner' || gameState === 'crashing') {
            e.preventDefault();
        }
    }, { passive: false });

    // =====================================================================
    //  BOOT
    // =====================================================================
    arcade.mount({
      title: 'Paperboy',
      help: 'W/↑ speeds up; S/↓ brakes; A/D or ←/→ steers. Space throws left; Enter or Shift throws right. Start a throw before you pass a red mailbox. Link deliveries for streak bonuses. Collect paper bundles on the road. P pauses; M toggles sound.',
      active: () => ['playing', 'banner', 'crashing'].includes(gameState),
      clearInput: () => Object.keys(keys).forEach(key => { keys[key] = false; }),
      restart: startNewGame,
      onResume: () => { lastTime = performance.now(); simAccumulator = 0; },
    });
    arcade.status('Deliver to red mailboxes. Brake to line up a throw.');
    updateHUD();
    updateMobileControlsVisibility();
    requestAnimationFrame(gameLoop);
